import {
  forwardRef,
  type ComponentPropsWithoutRef,
  type ElementType,
  type ReactNode,
  type Ref,
} from "react";

import { focusRing } from "../../foundations/a11y";
import { cn } from "../../lib/cn";

export type TileTone = "fill" | "surface";
export type TilePadding = "sm" | "md";

const tones: Record<TileTone, string> = {
  fill: "bg-fill",
  surface: "bg-surface",
};

const paddings: Record<TilePadding, string> = {
  sm: "px-4 py-3",
  md: "p-5",
};

const interactiveMotion =
  "w-full cursor-pointer text-left transition-[background-color,transform] duration-base ease-settle active:scale-[0.99] motion-reduce:transform-none motion-reduce:transition-none";

const interactiveToneHovers: Record<TileTone, string> = {
  fill: "hover:bg-fill-secondary",
  surface: "hover:bg-fill",
};

type TileOwnProps = {
  tone?: TileTone;
  padding?: TilePadding;
  interactive?: boolean;
  as?: ElementType;
  children?: ReactNode;
};

export type TileProps = TileOwnProps &
  Omit<ComponentPropsWithoutRef<"div">, keyof TileOwnProps>;

export const Tile = forwardRef<HTMLElement, TileProps>(function Tile(
  {
    tone = "fill",
    padding = "sm",
    interactive = false,
    as,
    className,
    children,
    ...rest
  },
  ref,
) {
  const Component = (as ?? "div") as ElementType;
  // Tile is borderless by design — surface definition comes from the fill tone,
  // not a border. If more separation is needed, deepen the fill (don't add a border).
  return (
    <Component
      ref={ref as Ref<never>}
      className={cn(
        "rounded-xl",
        tones[tone],
        paddings[padding],
        interactive && interactiveMotion,
        interactive && interactiveToneHovers[tone],
        interactive && focusRing,
        className,
      )}
      {...rest}
    >
      {children}
    </Component>
  );
});
